import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BillingAddressBody, PaymentMethodBody } from '@brimo/shared';
import { api } from '../../../lib/api';

/**
 * Types and hooks of the billing contract (docs/dev/api-billing.md, D-07): the user's billing
 * (`/settings/billing`, Einstellungen 18/19/M9, Onboarding Nutzer 10/15) and the organization's billing
 * (`/settings/organization/billing`, Einstellungen 28). Query root `['billing']` (realtime `billing.updated`).
 */

export interface ActionState {
  allowed: boolean;
  reason: string | null;
}

export type InvoiceStatus = 'open' | 'paid' | 'chargeback' | 'cancelled';

export interface Invoice {
  id: string;
  number: string;
  issuedOn: string;
  issuedOnLabel: string;
  amountLabel: string;
  status: InvoiceStatus;
  statusLabel: string;
  pdfUrl: string | null;
}

export interface BillingPlan {
  key: 'free' | 'team' | 'single';
  name: string;
  features: string | null;
  note: string | null;
  /** „79 € / Monat" */
  priceLabel: string;
  /** „79 € netto / Monat" */
  priceNetLabel: string;
  /** First debit day `YYYY-MM-DD`. */
  firstDebitOn: string;
}

export interface CompanyBilling {
  mode: 'company';
  title: string;
  text: string;
  /** „Berater · Vollzugang" */
  workplace: string;
  /** „M. Osterloh · 12.09.2026" or null. */
  assignedBy: string | null;
  footer: string;
}

export interface OwnBilling {
  mode: 'own';
  title: string;
  /** EM9 note. */
  notice: string;
  plan: BillingPlan;
  invoices: Invoice[];
  paymentMethod: null;
  billingAddress: string | null;
  vatId: string | null;
  deadlineOn: string | null;
  overdue: boolean;
  overdueNotice: { title: string; text: string } | null;
  companyBillingRequestedAt: string | null;
  paymentsActive: false;
  actions: {
    changePlan: ActionState;
    cancel: ActionState;
    addPaymentMethod: ActionState;
    changeAddress: ActionState;
    requestCompanyBilling: ActionState;
  };
}

export type UserBilling = CompanyBilling | OwnBilling;

export interface SeatSummary {
  used: number;
  total: number | null;
  selfPaying: number;
  label: string;
}

export interface OrganizationBilling {
  banner: { title: string; text: string } | null;
  plan: {
    key: 'free' | 'team' | 'single';
    status: 'free' | 'trial' | 'active' | 'dunned' | 'cancelled';
    name: string;
    cycle: string;
    usage: SeatSummary;
    priceLabel: string;
    grid: { label: string; value: string }[];
    trialEndsOn: string | null;
  };
  invoices: Invoice[];
  paymentMethod: null;
  invoiceEmail: string | null;
  invoiceEmailNote: string;
  actions: {
    payNow: ActionState;
    changePaymentMethod: ActionState;
    adjustSeats: ActionState;
    changePlan: ActionState;
    changeInvoiceEmail: ActionState;
  };
}

export const billingKeys = {
  root: ['billing'] as const,
  user: () => ['billing', 'user'] as const,
  organization: () => ['billing', 'organization'] as const,
};

export function useUserBilling(enabled = true) {
  return useQuery({
    queryKey: billingKeys.user(),
    queryFn: () => api<UserBilling>('/settings/billing'),
    enabled,
  });
}

export function useOrganizationBilling() {
  return useQuery({
    queryKey: billingKeys.organization(),
    queryFn: () => api<OrganizationBilling>('/settings/organization/billing'),
    retry: false,
  });
}

/** `POST /settings/billing/payment-method`: validated by the server, then refused with 409 (D-07). */
export function submitPaymentMethod(body: PaymentMethodBody): Promise<void> {
  return api<void>('/settings/billing/payment-method', { method: 'POST', body });
}

export function useSaveBillingAddress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BillingAddressBody) =>
      api<UserBilling>('/settings/billing/address', { method: 'PUT', body }),
    onSuccess: (data) => queryClient.setQueryData(billingKeys.user(), data),
  });
}

export function useRequestCompanyBilling() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<UserBilling>('/settings/billing/company-request', { method: 'POST', body: {} }),
    onSuccess: (data) => queryClient.setQueryData(billingKeys.user(), data),
  });
}

export function useSaveInvoiceEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      api<OrganizationBilling>('/settings/organization/billing/invoice-email', {
        method: 'PUT',
        body: { email },
      }),
    onSuccess: (data) => queryClient.setQueryData(billingKeys.organization(), data),
  });
}
